import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import Summary from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import Image from '../../widgets/Image';

import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import WhySitePen from '../../widgets/WhySitePen';

const backendToolsImg = require('../../assets/images/icon-illustration/back-end-tools.svg');
const codeAlertImg = require('../../assets/images/illustration/code-alert.svg');
const consultingImg = require('../../assets/images/illustration/consulting.svg');
const highPerformanceImg = require('../../assets/images/illustration/high-performance.svg');
const managementImg = require('../../assets/images/illustration/management.svg');
const reduceCostsImg = require('../../assets/images/icon-illustration/reduce-costs.svg');
const securityImg = require('../../assets/images/icon-illustration/security-testing.svg');

const factory = create();

const SoftwareModernization = factory(function SoftwareModernization() {
	return (
		<div>
			<Head
				title="Legacy Software Modernization Services | SitePen"
				description="SitePen offers legacy software and systems modernization to upgrade clients to the modern systems to improve processes, streamline operations, and empower business success."
			/>

			<Hero background="level3Yellow">
				{{
					xlTitle: (
						<div>
							Legacy Software
							<br />
							Modernization
						</div>
					)
				}}
			</Hero>

			<Section>
				<ContentWrapper>
					<Summary reverse>
						{{
							title: 'Modernize Your Business',
							blurb: (
								<virtual>
									An ever-increasing reliance on software to conduct business
									coupled with a continually evolving technology industry means
									organizations need some form of investment in their software
									products to stay competitive. Users are becoming more
									sophisticated in their expectations of what a software product
									should offer, whether using ever-more-complex traditional
									desktop applications or desiring more location- and
									device-independent offerings through web applications. Mobile
									device prevalence adds a further layer where users expect access
									to some or all of their software’s functionality through an app
									on their phone. Organizations that keep up to date with or even
									drive evolution in user and market trends for software products
									can expect deeper engagement with their customers and broader
									market reach.
								</virtual>
							),
							image: <Image src={consultingImg} size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<Summary paddingBottom="md">
						{{
							title: 'What Is Legacy Software?',
							blurb: (
								<virtual>
									Legacy software can mean several things. Systems that were
									designed and implemented before modern usage paradigms (web,
									mobile) or implemented using outdated or unsupported technology
									stacks can be considered legacy. Poorly implemented software
									that provides a substandard user experience or is costly to
									operate can also be considered legacy. Changes in markets, legal
									requirements, user expectations, or competition can even move
									otherwise perfectly valid solutions into a “legacy” state if the
									systems cannot easily be adapted to meet the new requirements.
								</virtual>
							),
							image: <Image src={codeAlertImg} lazy size="lg" />
						}}
					</Summary>
					<Summary reverse paddingBottom="md">
						{{
							title: 'What Is Legacy Software Modernization?',
							blurb: (
								<virtual>
									Modernizing legacy software is a range of processes around
									enhancing, updating, or replacing a software system with
									something that meets updated requirements, is more engaging to
									use, or is cheaper to maintain, operate, and scale. There are
									many activities included within this concept - the chosen
									activities for a given modernization effort depend on the
									desired outcome and budget availability. These activities can
									vary from continuous, ongoing improvement in a business’s
									software products; occasional, infrequent enhancement spikes to
									improve certain areas; through to completely replacing aging or
									costly systems with more modern, cost-effective alternatives.
								</virtual>
							),
							image: <Image src={highPerformanceImg} lazy size="lg" />
						}}
					</Summary>
					<WhySitePen>
						{{
							title: 'Why Choose SitePen?',
							blurb: (
								<virtual>
									SitePen has extensive experience in designing and implementing
									software applications at the leading edge of user engagement
									paradigms. Since inception, our focus has been creating
									exceptional web applications that delight users; however, our
									expertise extends beyond this to include desktop and mobile
									application development, including uplift and migration of
									systems across these paradigms. Many of our enterprise clients
									have engaged us for our ability to analyze existing legacy
									systems and design and implement modern web-focused replacements
									that drive improved customer experience and engagement.
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Benefits of Legacy Software Modernization',
							items: [
								{
									title: 'Reduced Costs',
									blurb: (
										<p>
											Certain legacy software may have never been designed to
											scale horizontally. When trying to increase such a
											system’s capacity, businesses need to purchase larger
											and exponentially more costly individual machines to
											scale vertically instead. Additionally, finding people
											with the necessary skills to enhance or operate the
											legacy system from a dwindling pool of resources
											requires significant investment in a seller’s market.
											Such factors can quickly outpace the equivalent costs of
											implementing and operating a more efficient, up-to-date
											replacement system.
										</p>
									),
									image: (
										<Image
											src={reduceCostsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Improved Security',
									blurb: (
										<p>
											Trends in application security are also constantly
											changing in line with advancements in privacy laws
											across the globe. Users expect to have their data held
											and operated securely, in line with their local security
											legislation. Advances in computational power and
											security research also mean previously-secure encryption
											algorithms need replacing with updated alternatives over
											time. Keeping up with these types of security-related
											software modernization efforts is increasingly becoming
											a legal requirement for most organizations.
										</p>
									),
									image: (
										<Image src={securityImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Operational Efficiency',
									blurb: (
										<p>
											Modern trends around compute and storage virtualization
											have opened up more cost-effective ways to host and
											operate software systems. Companies can make more
											effective use of their existing hardware, achieving
											higher ongoing utilization and reducing overspend on
											unneeded infrastructure, or they can outsource
											infrastructure and middleware system management entirely
											to third-party cloud vendors. This direction has opened
											up new ways of developing systems that are simpler to
											implement and scale. However, legacy systems need to be
											adapted or replaced to use these advances in runtime
											simplification and hosting options.
										</p>
									),
									image: (
										<Image
											src={backendToolsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={managementImg} lazy size="lg" />,
							title: 'Our Software & Systems Modernization Process',
							blurb: (
								<virtual>
									At SitePen, our focus is on helping our clients to provide the
									best experience for their end-users. We are passionate about
									designing and implementing software solutions that engage users
									and make performing their tasks more enjoyable and productive.
									Our work includes analyzing legacy systems to identify
									improvement areas where minimum investment can realize maximum
									increases in user satisfaction and operational efficiency. We
									explore a range of alternatives that enhance existing systems
									where possible or migrate to new solutions that better meet
									updated requirements. Cost management is also key, with all
									possible modernization options weighed against the investment
									needed to achieve them and structured as phased deliverables to
									realize value throughout a modernization project. The assembled
									matrix forms the basis for selecting the most appropriate
									modernization approach that best meets your needs and budget.
									With a strategy agreed, SitePen can begin work with your
									technology teams or as a standalone delivery function to
									implement the desired modernization plan.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>

			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Software Modernization FAQs">
					{[
						{
							title: 'What Types of Businesses Should Update Their Software?',
							content: (
								<p>
									Any business that relies on decades-old software should keep a
									close eye on the ongoing costs required to maintain their legacy
									systems and weigh such costs against those needed for
									modernization efforts to see if cost saving over the longer term
									is possible. Businesses with software systems that may be
									satisfactory for current needs but do not allow them to reach
									new market opportunities can use modernization to extend their
									systems appropriately. Other types of businesses that would
									benefit from modernization are those with software systems that
									are not user-friendly or counterproductive to user efficiency -
									modernization can help improve user experience and engagement in
									such systems with comparably little investment required.
								</p>
							)
						},
						{
							title: 'How Long Does Modernization Take?',
							content: (
								<p>
									The time required to modernize a system depends entirely on the
									chosen modernization strategy. On the upper end of the scale,
									this would be equal to building a new replacement system,
									including any migration required between old and new systems
									once the new system is operational. More straightforward
									strategies such as smaller enhancements on existing systems will
									typically require less time. Whichever approach is chosen, it is
									very likely that some phasing is possible, meaning modernization
									efforts can deliver value incrementally without having to wait
									for a big-bang replacement.
								</p>
							)
						},
						{
							title: 'How Much Does Software Modernization Cost?',
							content: (
								<p>
									Similar to time requirements, costs for software modernization
									can vary significantly and depend on which modernization
									strategy is chosen. As costs can quickly spiral, careful cost
									analysis and management are required to keep modernization
									efforts in line with budgets. An important factor to determine
									is the “cost tipping point” where cost of whichever chosen
									migration strategy falls below that of the ongoing maintenance
									cost of operating the legacy system. Modernizing a system may
									also open up new market opportunities, so the exact cost tipping
									point can vary backwards and forwards on a budget timeline and
									requires careful analysis to determine correctly. Organizations
									should start planning for modernization efforts well in advance
									of reaching their cost tipping point.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default SoftwareModernization;
